import { logger } from "./Logger";
import { estimateTokenCount } from "./TokenCounter";

/**
 * SmartContextExtractor - Hybrid VSCode-like Search + Smart Context Extraction
 *
 * ALGORITHM OVERVIEW:
 * ==================
 *
 * This implementation uses a two-phase hybrid approach:
 *
 * PHASE 1: Guaranteed Search Results (VSCode-like)
 * ------------------------------------------------
 * 1. Search the entire codebase using case-insensitive partial matching (like VSCode with ExactMatch enabled)
 *    - "undo" matches: undo, Undo, UNDO, undoManager, handleUndoOperation, etc.
 * 2. For each search result, include the matching line + 4 lines above and 4 lines below (default)
 * 3. Check if this fits within the token limit
 * 4. If exceeds limit, progressively reduce context:
 *    - Alternate between reducing top and bottom context lines
 *    - 4↑4↓ → 3↑4↓ → 3↑3↓ → 2↑3↓ → 2↑2↓ → 1↑2↓ → 1↑1↓ → 0↑1↓ → 0↑0↓ (only matching lines)
 * 5. If even just matching lines exceed token limit, fall back to Phase 2
 *
 * PHASE 2: Smart Context Extraction (Remaining Budget)
 * ---------------------------------------------------
 * 1. Use remaining token budget for intelligent context extraction
 * 2. Extract relevant code units, functions, classes around the search results
 * 3. If smart extraction exceeds remaining budget, remove lowest-scoring content
 *
 * GUARANTEES:
 * - Every search match is included (unless token budget is impossibly small)
 * - VSCode-like search behavior with partial word matching
 * - Optimal use of available token budget
 * - Predictable and deterministic results
 */

interface SearchMatch {
  filePath: string;
  lineNumber: number;
  line: string;
  columnStart: number;
  columnEnd: number;
  keyword: string;
}

interface GuaranteedSection {
  filePath: string;
  startLine: number;
  endLine: number;
  matches: SearchMatch[];
  content: string;
  tokenCount: number;
}

interface SmartContextSection {
  filePath: string;
  content: string;
  tokenCount: number;
  relevanceScore: number;
  type: "function" | "class" | "interface" | "block";
  expandedRange?: { start: number; end: number }; // For deduplication with guaranteed sections
}

interface ExtractedFile {
  path: string;
  content: string;
  fileName: string;
  type: string;
  isSnippet: boolean;
  originalTokenCount: number;
  snippetTokenCount: number;
  relevanceScore: number;
  matchCount: number;
  guaranteedMatches: number;
  smartContextAdded: boolean;
  lineRanges: Array<{
    start: number;
    end: number;
    type: "guaranteed" | "smart";
  }>;
}

export class SmartContextExtractor {
  // Token limits
  private readonly MAX_TOKENS_PER_REQUEST = 200000;
  private readonly MAX_TOKENS_PER_FILE = 20000;

  // Phase 1: Guaranteed search results context
  private readonly DEFAULT_CONTEXT_LINES_ABOVE = 4;
  private readonly DEFAULT_CONTEXT_LINES_BELOW = 4;
  private readonly MIN_CONTEXT_LINES = 0;

  // Phase 2: Smart context extraction
  private readonly MIN_TOKENS_FOR_SMART_EXTRACTION = 5000; // Only do smart extraction if we have decent budget left

  /**
   * Main entry point for hybrid context extraction
   */
  async extractRelevantContext(
    keywords: string[],
    documentMap: Map<string, any>,
    fileIndex: any,
    repoData: any
  ): Promise<ExtractedFile[]> {
    const startTime = Date.now();

    logger.debug.info(
      "DEBUGGING_AI_QUALITY: SmartContextExtractor - Starting Hybrid Approach",
      {
        inputKeywords: keywords,
        documentMapSize: documentMap?.size || 0,
        tokenBudget: this.MAX_TOKENS_PER_REQUEST,
        algorithm:
          "Phase 1: Guaranteed Search Results → Phase 2: Smart Context",
        timestamp: new Date().toISOString(),
      }
    );

    // PHASE 1: Guaranteed Search Results
    const { guaranteedSections, usedTokens } =
      await this.extractGuaranteedSearchResults(keywords, documentMap);

    const remainingTokens = this.MAX_TOKENS_PER_REQUEST - usedTokens;

    logger.debug.info(
      "DEBUGGING_AI_QUALITY: Phase 1 Complete - Guaranteed Search Results",
      {
        guaranteedSections: guaranteedSections.length,
        totalMatches: guaranteedSections.reduce(
          (sum, s) => sum + s.matches.length,
          0
        ),
        usedTokens,
        remainingTokens,
        phase1TimeMs: Date.now() - startTime,
      }
    );

    // PHASE 2: Smart Context Extraction (if we have remaining budget)
    let smartContextSections: SmartContextSection[] = [];
    if (remainingTokens > this.MIN_TOKENS_FOR_SMART_EXTRACTION) {
      smartContextSections = await this.extractSmartContext(
        keywords,
        documentMap,
        guaranteedSections,
        remainingTokens
      );

      logger.debug.info(
        "DEBUGGING_AI_QUALITY: Phase 2 Complete - Smart Context",
        {
          smartContextSections: smartContextSections.length,
          smartContextTokens: smartContextSections.reduce(
            (sum, s) => sum + s.tokenCount,
            0
          ),
          totalTokensUsed:
            usedTokens +
            smartContextSections.reduce((sum, s) => sum + s.tokenCount, 0),
        }
      );
    }

    // Combine both phases into final extracted files
    const extractedFiles = this.combineGuaranteedAndSmartContext(
      guaranteedSections,
      smartContextSections
    );

    const finalTokenCount = extractedFiles.reduce(
      (sum, f) => sum + f.snippetTokenCount,
      0
    );

    logger.debug.info(
      "DEBUGGING_AI_QUALITY: SmartContextExtractor - Final Results",
      {
        selectedFiles: extractedFiles.length,
        finalTokenCount,
        tokenBudget: this.MAX_TOKENS_PER_REQUEST,
        tokenUtilization: `${Math.round(
          (finalTokenCount / this.MAX_TOKENS_PER_REQUEST) * 100
        )}%`,
        guaranteedMatches: extractedFiles.reduce(
          (sum, f) => sum + f.guaranteedMatches,
          0
        ),
        smartContextFiles: extractedFiles.filter((f) => f.smartContextAdded)
          .length,
        processingTimeMs: Date.now() - startTime,
        timestamp: new Date().toISOString(),
      }
    );

    return extractedFiles;
  }

  /**
   * PHASE 1: Extract guaranteed search results with progressive context reduction
   */
  private async extractGuaranteedSearchResults(
    keywords: string[],
    documentMap: Map<string, any>
  ): Promise<{ guaranteedSections: GuaranteedSection[]; usedTokens: number }> {
    // Step 1: Find all search matches using VSCode-like search
    const allMatches = this.performVSCodeLikeSearch(keywords, documentMap);

    if (allMatches.length === 0) {
      logger.debug.warn("DEBUGGING_AI_QUALITY: No search matches found");
      return { guaranteedSections: [], usedTokens: 0 };
    }

    // Step 2: Group matches by file
    const matchesByFile = this.groupMatchesByFile(allMatches);

    // Step 3: Extract content with maximum context first
    let contextAbove = this.DEFAULT_CONTEXT_LINES_ABOVE;
    let contextBelow = this.DEFAULT_CONTEXT_LINES_BELOW;
    let guaranteedSections: GuaranteedSection[] = [];
    let totalTokens = 0;

    // Progressive context reduction loop
    while (contextAbove >= 0 && contextBelow >= 0) {
      guaranteedSections = this.extractGuaranteedSections(
        matchesByFile,
        documentMap,
        contextAbove,
        contextBelow
      );

      totalTokens = guaranteedSections.reduce(
        (sum, s) => sum + s.tokenCount,
        0
      );

      logger.debug.info(
        `DEBUGGING_AI_QUALITY: Testing context ${contextAbove}↑${contextBelow}↓`,
        {
          sections: guaranteedSections.length,
          totalTokens,
          withinBudget: totalTokens <= this.MAX_TOKENS_PER_REQUEST,
        }
      );

      if (totalTokens <= this.MAX_TOKENS_PER_REQUEST) {
        break; // Found a configuration that fits
      }

      // Reduce context alternating between top and bottom
      if (contextAbove > contextBelow) {
        contextAbove--;
      } else if (contextBelow > 0) {
        contextBelow--;
      } else if (contextAbove > 0) {
        contextAbove--;
      } else {
        break; // We're down to just matching lines
      }
    }

    // If even just matching lines exceed budget, we need to drop some matches
    if (totalTokens > this.MAX_TOKENS_PER_REQUEST) {
      logger.debug.warn(
        "DEBUGGING_AI_QUALITY: Even matching lines exceed budget, selecting highest priority matches"
      );
      guaranteedSections = this.selectHighestPriorityMatches(
        guaranteedSections,
        this.MAX_TOKENS_PER_REQUEST * 0.8 // Reserve 20% for potential smart context
      );
      totalTokens = guaranteedSections.reduce(
        (sum, s) => sum + s.tokenCount,
        0
      );
    }

    return { guaranteedSections, usedTokens: totalTokens };
  }

  /**
   * Perform VSCode-like case-insensitive partial word matching search
   */
  private performVSCodeLikeSearch(
    keywords: string[],
    documentMap: Map<string, any>
  ): SearchMatch[] {
    const allMatches: SearchMatch[] = [];
    let filesSearched = 0;

    // Log available documents
    const allPaths = Array.from(documentMap.values())
      .map((doc) => doc?.path)
      .filter((path) => path);

    logger.debug.info("DEBUGGING_AI_QUALITY: VSCode-like Search Starting", {
      keywords,
      totalDocuments: documentMap.size,
      hasAppJs: allPaths.some((path) => path?.includes("App.js")),
      appJsPaths: allPaths
        .filter((path) => path?.includes("App.js"))
        .slice(0, 5),
    });

    for (const [docId, document] of documentMap) {
      if (!document?.content || !document.path) {
        continue;
      }

      filesSearched++;
      const content = this.normalizeContent(document.content);
      const lines = content.split("\n");

      // Search each keyword in this document
      for (const keyword of keywords) {
        const searchPattern = this.createVSCodeSearchPattern(keyword);

        lines.forEach((line, lineIndex) => {
          let match;
          searchPattern.lastIndex = 0;

          while ((match = searchPattern.exec(line)) !== null) {
            allMatches.push({
              filePath: document.path,
              lineNumber: lineIndex + 1,
              line: line,
              columnStart: match.index,
              columnEnd: match.index + match[0].length,
              keyword: keyword,
            });

            // Special logging for important matches
            if (line.includes("undoManagerOperation")) {
              logger.debug.info(
                "DEBUGGING_AI_QUALITY: Found undoManagerOperation!",
                {
                  filePath: document.path,
                  lineNumber: lineIndex + 1,
                  keyword,
                  matchedText: match[0],
                  line: line.substring(0, 100),
                }
              );
            }
          }
        });
      }
    }

    logger.debug.info("DEBUGGING_AI_QUALITY: VSCode-like Search Complete", {
      filesSearched,
      totalMatches: allMatches.length,
      matchesByKeyword: keywords.map((k) => ({
        keyword: k,
        matches: allMatches.filter((m) => m.keyword === k).length,
      })),
    });

    return allMatches;
  }

  /**
   * Create VSCode-like search pattern (case-insensitive, partial word matching)
   */
  private createVSCodeSearchPattern(keyword: string): RegExp {
    // Escape special regex characters
    const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    // VSCode behavior: word boundary at start only for partial matching
    const startsWithWord = /^\w/.test(keyword);
    let pattern = escaped;

    if (startsWithWord) {
      pattern = "\\b" + pattern;
    }

    return new RegExp(pattern, "gi");
  }

  /**
   * Group matches by file path
   */
  private groupMatchesByFile(
    matches: SearchMatch[]
  ): Map<string, SearchMatch[]> {
    const grouped = new Map<string, SearchMatch[]>();

    for (const match of matches) {
      if (!grouped.has(match.filePath)) {
        grouped.set(match.filePath, []);
      }
      grouped.get(match.filePath)!.push(match);
    }

    return grouped;
  }

  /**
   * Extract guaranteed sections with specified context lines
   */
  private extractGuaranteedSections(
    matchesByFile: Map<string, SearchMatch[]>,
    documentMap: Map<string, any>,
    contextAbove: number,
    contextBelow: number
  ): GuaranteedSection[] {
    const sections: GuaranteedSection[] = [];

    for (const [filePath, matches] of matchesByFile) {
      const document = Array.from(documentMap.values()).find(
        (doc) => doc.path === filePath
      );

      if (!document) continue;

      const content = this.normalizeContent(document.content);
      const lines = content.split("\n");

      // Calculate line ranges for all matches in this file
      const lineRanges = this.calculateGuaranteedLineRanges(
        matches,
        lines.length,
        contextAbove,
        contextBelow
      );

      // Extract content for these ranges
      const extractedContent = this.extractLineRangesWithMetadata(
        lines,
        lineRanges,
        filePath,
        matches
      );

      sections.push({
        filePath,
        startLine: Math.min(...lineRanges.map((r) => r.start)),
        endLine: Math.max(...lineRanges.map((r) => r.end)),
        matches,
        content: extractedContent,
        tokenCount: estimateTokenCount(extractedContent),
      });
    }

    return sections;
  }

  /**
   * Calculate line ranges ensuring all matches have context
   */
  private calculateGuaranteedLineRanges(
    matches: SearchMatch[],
    totalLines: number,
    contextAbove: number,
    contextBelow: number
  ): Array<{ start: number; end: number }> {
    const ranges: Array<{ start: number; end: number }> = [];

    // Sort matches by line number
    const sortedMatches = [...matches].sort(
      (a, b) => a.lineNumber - b.lineNumber
    );

    for (const match of sortedMatches) {
      const rangeStart = Math.max(1, match.lineNumber - contextAbove);
      const rangeEnd = Math.min(totalLines, match.lineNumber + contextBelow);

      // Merge overlapping ranges
      if (ranges.length > 0) {
        const lastRange = ranges[ranges.length - 1];
        if (rangeStart <= lastRange.end + 1) {
          lastRange.end = Math.max(lastRange.end, rangeEnd);
          continue;
        }
      }

      ranges.push({ start: rangeStart, end: rangeEnd });
    }

    return ranges;
  }

  /**
   * Extract content for line ranges with metadata
   */
  private extractLineRangesWithMetadata(
    lines: string[],
    lineRanges: Array<{ start: number; end: number }>,
    filePath: string,
    matches: SearchMatch[]
  ): string {
    const sections: string[] = [];

    sections.push(
      `// File: ${filePath}`,
      `// Guaranteed search results: ${matches.length} matches in ${lineRanges.length} section(s)`,
      ""
    );

    for (let i = 0; i < lineRanges.length; i++) {
      const range = lineRanges[i];

      if (i > 0) {
        sections.push("", "// ... (omitted lines) ...", "");
      }

      sections.push(`// Lines ${range.start}-${range.end}:`);
      const extractedLines = lines.slice(range.start - 1, range.end);
      sections.push(...extractedLines);
    }

    return sections.join("\n");
  }

  /**
   * Select highest priority matches if we're over budget
   */
  private selectHighestPriorityMatches(
    sections: GuaranteedSection[],
    maxTokens: number
  ): GuaranteedSection[] {
    // Sort by number of matches (more matches = higher priority)
    const sorted = [...sections].sort(
      (a, b) => b.matches.length - a.matches.length
    );

    const selected: GuaranteedSection[] = [];
    let usedTokens = 0;

    for (const section of sorted) {
      if (usedTokens + section.tokenCount <= maxTokens) {
        selected.push(section);
        usedTokens += section.tokenCount;
      }
    }

    logger.debug.info(
      "DEBUGGING_AI_QUALITY: Selected highest priority matches",
      {
        originalSections: sections.length,
        selectedSections: selected.length,
        usedTokens,
        maxTokens,
      }
    );

    return selected;
  }

  /**
   * PHASE 2: Extract smart context to fill remaining token budget
   * Algorithm: Collect ALL candidates → Sort by relevance → Fill budget from highest relevance first
   */
  private async extractSmartContext(
    keywords: string[],
    documentMap: Map<string, any>,
    guaranteedSections: GuaranteedSection[],
    remainingTokens: number
  ): Promise<SmartContextSection[]> {
    logger.debug.info(
      "DEBUGGING_AI_QUALITY: Starting Smart Context Extraction",
      {
        remainingTokens,
        guaranteedFiles: guaranteedSections.length,
        algorithm:
          "Collect ALL candidates → Sort by relevance → Fill from highest relevance",
      }
    );

    // STEP 1: Collect ALL candidate smart context sections (NO token limit checks during collection)
    const allCandidates: SmartContextSection[] = [];

    // Get files that already have guaranteed sections (to avoid duplication)
    const guaranteedFilePaths = new Set(
      guaranteedSections.map((s) => s.filePath)
    );

    // Strategy 1: Extract expanded context around matches in guaranteed files
    // We'll deduplicate overlapping lines later
    logger.debug.info(
      "DEBUGGING_AI_QUALITY: Collecting expanded context candidates (will deduplicate later)..."
    );
    for (const section of guaranteedSections) {
      const expandedContext = this.extractExpandedContext(
        section,
        documentMap,
        Number.MAX_SAFE_INTEGER // No limit during collection phase
      );

      if (expandedContext && expandedContext.tokenCount > 0) {
        allCandidates.push(expandedContext);
      }
    }

    // Strategy 2: Search for keyword-related content in other files
    logger.debug.info(
      "DEBUGGING_AI_QUALITY: Collecting related content candidates from other files..."
    );
    const relatedSections = this.extractRelatedContent(
      keywords,
      documentMap,
      guaranteedFilePaths,
      Number.MAX_SAFE_INTEGER // No limit during collection phase
    );
    allCandidates.push(...relatedSections);

    logger.debug.info("DEBUGGING_AI_QUALITY: All candidates collected", {
      totalCandidates: allCandidates.length,
      expandedContextCandidates: allCandidates.filter((c) => c.expandedRange)
        .length,
      relatedContentCandidates: allCandidates.filter((c) => !c.expandedRange)
        .length,
      guaranteedFilesWithExpandedContext: guaranteedFilePaths.size,
      totalCandidateTokens: allCandidates.reduce(
        (sum, c) => sum + c.tokenCount,
        0
      ),
    });

    // STEP 2: Sort ALL candidates by relevance score (highest first)
    allCandidates.sort((a, b) => b.relevanceScore - a.relevanceScore);

    logger.debug.info("DEBUGGING_AI_QUALITY: Candidates sorted by relevance", {
      topCandidates: allCandidates.slice(0, 10).map((c) => ({
        filePath: c.filePath,
        relevanceScore: c.relevanceScore.toFixed(3),
        tokenCount: c.tokenCount,
        type: c.type,
      })),
    });

    // STEP 3: Fill remaining token budget starting from highest relevance
    const selectedSections: SmartContextSection[] = [];
    let usedTokens = 0;

    for (const candidate of allCandidates) {
      if (usedTokens + candidate.tokenCount <= remainingTokens) {
        selectedSections.push(candidate);
        usedTokens += candidate.tokenCount;

        logger.debug.info(`DEBUGGING_AI_QUALITY: Selected smart context`, {
          filePath: candidate.filePath,
          relevanceScore: candidate.relevanceScore.toFixed(3),
          tokenCount: candidate.tokenCount,
          type: candidate.type,
          totalUsedTokens: usedTokens,
          remainingBudget: remainingTokens - usedTokens,
        });
      } else {
        // Log first few skipped candidates
        if (
          selectedSections.length < allCandidates.length &&
          allCandidates.indexOf(candidate) < selectedSections.length + 5
        ) {
          logger.debug.info(
            `DEBUGGING_AI_QUALITY: Skipped candidate (would exceed budget)`,
            {
              filePath: candidate.filePath,
              relevanceScore: candidate.relevanceScore.toFixed(3),
              tokenCount: candidate.tokenCount,
              wouldExceedBy:
                usedTokens + candidate.tokenCount - remainingTokens,
            }
          );
        }
      }
    }

    // STEP 4: Deduplicate overlapping lines between guaranteed and smart context sections
    const deduplicatedSections = this.deduplicateSmartContextSections(
      selectedSections,
      guaranteedSections
    );

    logger.debug.info(
      "DEBUGGING_AI_QUALITY: Smart Context Extraction Complete",
      {
        totalCandidates: allCandidates.length,
        selectedSections: selectedSections.length,
        deduplicatedSections: deduplicatedSections.length,
        usedTokens,
        remainingTokens,
        budgetUtilization: `${Math.round(
          (usedTokens / remainingTokens) * 100
        )}%`,
        averageRelevanceScore:
          deduplicatedSections.length > 0
            ? (
                deduplicatedSections.reduce(
                  (sum, s) => sum + s.relevanceScore,
                  0
                ) / deduplicatedSections.length
              ).toFixed(3)
            : "0.000",
        skippedDueToTokenLimit: allCandidates.length - selectedSections.length,
        removedDueToDuplication:
          selectedSections.length - deduplicatedSections.length,
      }
    );

    return deduplicatedSections;
  }

  /**
   * Extract expanded context around existing guaranteed sections
   */
  private extractExpandedContext(
    guaranteedSection: GuaranteedSection,
    documentMap: Map<string, any>,
    maxTokens: number
  ): SmartContextSection | null {
    const document = Array.from(documentMap.values()).find(
      (doc) => doc.path === guaranteedSection.filePath
    );

    if (!document) return null;

    const content = this.normalizeContent(document.content);
    const lines = content.split("\n");

    // Try to expand context by finding function/class boundaries
    const expandedStart = this.findFunctionStart(
      lines,
      guaranteedSection.startLine - 1
    );
    const expandedEnd = this.findFunctionEnd(
      lines,
      guaranteedSection.endLine - 1
    );

    // Store the expanded range for deduplication
    const expandedContent = `// File: ${
      guaranteedSection.filePath
    } (Smart Context - Expanded)\n// Lines ${expandedStart + 1}-${
      expandedEnd + 1
    }:\n${lines.slice(expandedStart, expandedEnd + 1).join("\n")}`;

    const tokenCount = estimateTokenCount(expandedContent);

    if (tokenCount > maxTokens) {
      return null; // Too large for budget
    }

    return {
      filePath: guaranteedSection.filePath,
      content: expandedContent,
      tokenCount,
      relevanceScore: 0.8, // High relevance since it's expanding existing matches
      type: "function",
      expandedRange: { start: expandedStart + 1, end: expandedEnd + 1 }, // Store for deduplication
    };
  }

  /**
   * Find the start of a function/class containing the given line
   */
  private findFunctionStart(lines: string[], fromLine: number): number {
    for (let i = fromLine; i >= 0; i--) {
      const line = lines[i].trim();
      // Look for function/class/interface declarations
      if (
        line.match(
          /^(function|class|interface|export\s+(function|class|interface)|\/\*\*)/
        )
      ) {
        return i;
      }
      // Stop at empty lines followed by function-like patterns
      if (
        i > 0 &&
        !line &&
        lines[i + 1]?.trim().match(/^(function|class|interface|export)/)
      ) {
        return i;
      }
    }
    return Math.max(0, fromLine - 20); // Fallback: go back 20 lines
  }

  /**
   * Find the end of a function/class containing the given line
   */
  private findFunctionEnd(lines: string[], fromLine: number): number {
    let braceCount = 0;
    let inFunction = false;

    for (let i = fromLine; i < lines.length; i++) {
      const line = lines[i];

      // Count braces to find function boundaries
      for (const char of line) {
        if (char === "{") {
          braceCount++;
          inFunction = true;
        } else if (char === "}") {
          braceCount--;
          if (inFunction && braceCount === 0) {
            return i;
          }
        }
      }

      // Stop at next function declaration if we haven't found closing brace
      if (
        i > fromLine + 5 &&
        line
          .trim()
          .match(
            /^(function|class|interface|export\s+(function|class|interface))/
          )
      ) {
        return i - 1;
      }
    }

    return Math.min(lines.length - 1, fromLine + 30); // Fallback: go forward 30 lines
  }

  /**
   * Extract related content from other files
   */
  private extractRelatedContent(
    keywords: string[],
    documentMap: Map<string, any>,
    excludeFilePaths: Set<string>,
    maxTokens: number
  ): SmartContextSection[] {
    const relatedSections: SmartContextSection[] = [];

    // Look for files that mention keywords but weren't included in guaranteed results
    for (const [docId, document] of documentMap) {
      if (
        !document?.content ||
        !document.path ||
        excludeFilePaths.has(document.path)
      ) {
        continue;
      }

      const content = this.normalizeContent(document.content);
      const lines = content.split("\n");

      // Count keyword mentions
      let keywordMatches = 0;
      const matchingLines: number[] = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        for (const keyword of keywords) {
          const pattern = this.createVSCodeSearchPattern(keyword);
          if (pattern.test(line)) {
            keywordMatches++;
            matchingLines.push(i);
          }
        }
      }

      // If file has keyword mentions, extract relevant sections
      if (keywordMatches > 0) {
        const sections = this.extractRelevantSections(
          lines,
          matchingLines,
          document.path
        );

        for (const section of sections) {
          if (section.tokenCount <= maxTokens * 0.1) {
            // Don't let any single file dominate
            relatedSections.push({
              ...section,
              relevanceScore: keywordMatches / lines.length, // Simple relevance score
            });
          }
        }
      }
    }

    return relatedSections;
  }

  /**
   * Extract relevant sections around matching lines
   */
  private extractRelevantSections(
    lines: string[],
    matchingLines: number[],
    filePath: string
  ): SmartContextSection[] {
    const sections: SmartContextSection[] = [];

    // Group nearby matches
    const groups: number[][] = [];
    let currentGroup: number[] = [];

    for (const lineNum of matchingLines.sort((a, b) => a - b)) {
      if (
        currentGroup.length === 0 ||
        lineNum - currentGroup[currentGroup.length - 1] <= 10
      ) {
        currentGroup.push(lineNum);
      } else {
        groups.push(currentGroup);
        currentGroup = [lineNum];
      }
    }
    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    // Extract context for each group
    for (const group of groups) {
      const start = Math.max(0, group[0] - 3);
      const end = Math.min(lines.length - 1, group[group.length - 1] + 3);

      const sectionLines = lines.slice(start, end + 1);
      const content = `// File: ${filePath} (Smart Context - Related)\n// Lines ${
        start + 1
      }-${end + 1}:\n${sectionLines.join("\n")}`;

      sections.push({
        filePath,
        content,
        tokenCount: estimateTokenCount(content),
        relevanceScore: group.length / 10, // More matches = higher relevance
        type: "block",
      });
    }

    return sections;
  }

  /**
   * Combine guaranteed and smart context sections into final extracted files
   */
  private combineGuaranteedAndSmartContext(
    guaranteedSections: GuaranteedSection[],
    smartContextSections: SmartContextSection[]
  ): ExtractedFile[] {
    const extractedFiles: ExtractedFile[] = [];
    const fileMap = new Map<string, ExtractedFile>();

    // Convert guaranteed sections to extracted files
    for (const section of guaranteedSections) {
      const extractedFile: ExtractedFile = {
        path: section.filePath,
        content: section.content,
        fileName: section.filePath.split("/").pop() || "",
        type: "blob",
        isSnippet: true,
        originalTokenCount: section.tokenCount,
        snippetTokenCount: section.tokenCount,
        relevanceScore: this.calculateRelevanceScore(
          section.matches.length,
          section.content.length
        ),
        matchCount: section.matches.length,
        guaranteedMatches: section.matches.length,
        smartContextAdded: false,
        lineRanges: [
          {
            start: section.startLine,
            end: section.endLine,
            type: "guaranteed" as const,
          },
        ],
      };

      fileMap.set(section.filePath, extractedFile);
      extractedFiles.push(extractedFile);
    }

    // Add smart context sections
    for (const smartSection of smartContextSections) {
      const existingFile = fileMap.get(smartSection.filePath);

      if (existingFile) {
        // Merge with existing guaranteed section
        existingFile.content += "\n\n" + smartSection.content;
        existingFile.snippetTokenCount += smartSection.tokenCount;
        existingFile.smartContextAdded = true;
        existingFile.lineRanges.push({
          start: 1, // Smart context doesn't have specific line ranges
          end: 1,
          type: "smart" as const,
        });
        // Update relevance score (weighted average)
        existingFile.relevanceScore =
          existingFile.relevanceScore * 0.7 + smartSection.relevanceScore * 0.3;
      } else {
        // Create new file for smart context only
        const smartFile: ExtractedFile = {
          path: smartSection.filePath,
          content: smartSection.content,
          fileName: smartSection.filePath.split("/").pop() || "",
          type: "blob",
          isSnippet: true,
          originalTokenCount: smartSection.tokenCount,
          snippetTokenCount: smartSection.tokenCount,
          relevanceScore: smartSection.relevanceScore,
          matchCount: 0,
          guaranteedMatches: 0,
          smartContextAdded: true,
          lineRanges: [
            {
              start: 1,
              end: 1,
              type: "smart" as const,
            },
          ],
        };

        fileMap.set(smartSection.filePath, smartFile);
        extractedFiles.push(smartFile);
      }
    }

    // Sort by relevance score (guaranteed matches first, then by score)
    extractedFiles.sort((a, b) => {
      if (a.guaranteedMatches !== b.guaranteedMatches) {
        return b.guaranteedMatches - a.guaranteedMatches;
      }
      return b.relevanceScore - a.relevanceScore;
    });

    return extractedFiles;
  }

  /**
   * Calculate relevance score
   */
  private calculateRelevanceScore(
    matchCount: number,
    contentLength: number
  ): number {
    const matchScore = Math.log(matchCount + 1) / Math.log(10);
    const densityScore = Math.min(matchCount / (contentLength / 1000), 1);
    return matchScore * 0.7 + densityScore * 0.3;
  }

  /**
   * Normalize content to handle different formats
   */
  private normalizeContent(content: any): string {
    if (typeof content === "string") {
      return content;
    }
    if (Array.isArray(content)) {
      return content.join("\n");
    }
    return String(content);
  }

  /**
   * Deduplicate smart context sections by removing overlapping lines with guaranteed sections
   */
  private deduplicateSmartContextSections(
    smartContextSections: SmartContextSection[],
    guaranteedSections: GuaranteedSection[]
  ): SmartContextSection[] {
    const deduplicatedSections: SmartContextSection[] = [];

    // Create a map of guaranteed sections by file path for quick lookup
    const guaranteedSectionsByFile = new Map<string, GuaranteedSection>();
    for (const section of guaranteedSections) {
      guaranteedSectionsByFile.set(section.filePath, section);
    }

    logger.debug.info(
      "DEBUGGING_AI_QUALITY: Starting deduplication of smart context sections",
      {
        originalSmartSections: smartContextSections.length,
        guaranteedFiles: guaranteedSectionsByFile.size,
      }
    );

    for (const smartSection of smartContextSections) {
      const guaranteedSection = guaranteedSectionsByFile.get(
        smartSection.filePath
      );

      if (!guaranteedSection) {
        // No guaranteed section for this file, keep the smart context as-is
        deduplicatedSections.push(smartSection);
        logger.debug.info(
          `DEBUGGING_AI_QUALITY: Keeping smart context (no guaranteed section)`,
          {
            filePath: smartSection.filePath,
            type: smartSection.type,
          }
        );
        continue;
      }

      // Check for overlapping line ranges
      if (smartSection.expandedRange) {
        const smartStart = smartSection.expandedRange.start;
        const smartEnd = smartSection.expandedRange.end;
        const guaranteedStart = guaranteedSection.startLine;
        const guaranteedEnd = guaranteedSection.endLine;

        // Check if ranges overlap
        const overlaps =
          smartStart <= guaranteedEnd && smartEnd >= guaranteedStart;

        if (overlaps) {
          // Try to trim the overlapping parts and keep non-overlapping content
          const trimmedSection = this.trimOverlappingContent(
            smartSection,
            guaranteedSection
          );

          if (trimmedSection) {
            deduplicatedSections.push(trimmedSection);
            logger.debug.info(
              `DEBUGGING_AI_QUALITY: Trimmed overlapping smart context`,
              {
                filePath: smartSection.filePath,
                originalRange: `${smartStart}-${smartEnd}`,
                guaranteedRange: `${guaranteedStart}-${guaranteedEnd}`,
                trimmedTokens: trimmedSection.tokenCount,
                originalTokens: smartSection.tokenCount,
              }
            );
          } else {
            logger.debug.info(
              `DEBUGGING_AI_QUALITY: Removed smart context (completely overlapped)`,
              {
                filePath: smartSection.filePath,
                smartRange: `${smartStart}-${smartEnd}`,
                guaranteedRange: `${guaranteedStart}-${guaranteedEnd}`,
              }
            );
          }
        } else {
          // No overlap, keep the smart context as-is
          deduplicatedSections.push(smartSection);
          logger.debug.info(
            `DEBUGGING_AI_QUALITY: Keeping smart context (no overlap)`,
            {
              filePath: smartSection.filePath,
              smartRange: `${smartStart}-${smartEnd}`,
              guaranteedRange: `${guaranteedStart}-${guaranteedEnd}`,
            }
          );
        }
      } else {
        // Smart section doesn't have expandedRange (probably related content), keep it
        deduplicatedSections.push(smartSection);
        logger.debug.info(
          `DEBUGGING_AI_QUALITY: Keeping smart context (no expandedRange)`,
          {
            filePath: smartSection.filePath,
            type: smartSection.type,
          }
        );
      }
    }

    logger.debug.info("DEBUGGING_AI_QUALITY: Deduplication complete", {
      originalSections: smartContextSections.length,
      deduplicatedSections: deduplicatedSections.length,
      removedSections:
        smartContextSections.length - deduplicatedSections.length,
    });

    return deduplicatedSections;
  }

  /**
   * Trim overlapping content from smart context section
   */
  private trimOverlappingContent(
    smartSection: SmartContextSection,
    guaranteedSection: GuaranteedSection
  ): SmartContextSection | null {
    if (!smartSection.expandedRange) {
      return smartSection; // Can't trim without range info
    }

    const smartStart = smartSection.expandedRange.start;
    const smartEnd = smartSection.expandedRange.end;
    const guaranteedStart = guaranteedSection.startLine;
    const guaranteedEnd = guaranteedSection.endLine;

    // Extract lines from the smart section content
    const lines = smartSection.content.split("\n");
    let contentStartIndex = 0;
    let actualContentLines: string[] = [];

    // Find where the actual file content starts (skip header comments)
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].startsWith("// Lines ")) {
        contentStartIndex = i + 1;
        break;
      }
    }

    if (contentStartIndex < lines.length) {
      actualContentLines = lines.slice(contentStartIndex);
    } else {
      return null; // Couldn't parse content structure
    }

    // Calculate which lines to keep (before and after the overlap)
    const beforeOverlap: string[] = [];
    const afterOverlap: string[] = [];

    // Lines before the guaranteed section
    if (smartStart < guaranteedStart) {
      const beforeEndLine = Math.min(guaranteedStart - 1, smartEnd);
      const beforeLineCount = beforeEndLine - smartStart + 1;
      beforeOverlap.push(...actualContentLines.slice(0, beforeLineCount));
    }

    // Lines after the guaranteed section
    if (smartEnd > guaranteedEnd) {
      const afterStartLine = Math.max(guaranteedEnd + 1, smartStart);
      const offsetFromSmartStart = afterStartLine - smartStart;
      afterOverlap.push(...actualContentLines.slice(offsetFromSmartStart));
    }

    // If we have no non-overlapping content, return null
    if (beforeOverlap.length === 0 && afterOverlap.length === 0) {
      return null;
    }

    // Reconstruct the trimmed content
    const trimmedLines: string[] = [];
    const header = lines.slice(0, contentStartIndex);

    if (beforeOverlap.length > 0) {
      // Update header for before content
      const newBeforeHeader = header.map((line) => {
        if (line.includes("(Smart Context - Expanded)")) {
          return line.replace(
            "(Smart Context - Expanded)",
            "(Smart Context - Before Overlap)"
          );
        }
        if (line.startsWith("// Lines ")) {
          const beforeEnd = Math.min(guaranteedStart - 1, smartEnd);
          return `// Lines ${smartStart}-${beforeEnd}:`;
        }
        return line;
      });

      trimmedLines.push(...newBeforeHeader, ...beforeOverlap);
    }

    if (afterOverlap.length > 0) {
      if (beforeOverlap.length > 0) {
        trimmedLines.push("", "// ... (guaranteed section omitted) ...", "");
      }

      const afterStart = Math.max(guaranteedEnd + 1, smartStart);
      trimmedLines.push(`// Lines ${afterStart}-${smartEnd}:`, ...afterOverlap);
    }

    const trimmedContent = trimmedLines.join("\n");
    const newTokenCount = estimateTokenCount(trimmedContent);

    // Only return if we have meaningful content left
    if (newTokenCount < smartSection.tokenCount * 0.1) {
      return null; // Too little content left
    }

    return {
      ...smartSection,
      content: trimmedContent,
      tokenCount: newTokenCount,
      expandedRange: {
        start:
          beforeOverlap.length > 0
            ? smartStart
            : Math.max(guaranteedEnd + 1, smartStart),
        end:
          afterOverlap.length > 0
            ? smartEnd
            : Math.min(guaranteedStart - 1, smartEnd),
      },
    };
  }
}

// Export a singleton instance
export const smartContextExtractor = new SmartContextExtractor();
